import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import * as _ from 'lodash';
import {Page} from '../model/page';
import {saveAs} from '@progress/kendo-file-saver';

export class GeneralService {

    constructor(protected http: HttpClient) {
    }

    protected get apiUrl() {
        return location.origin; // environment.API_URL;
    }

    protected handleError(error: Response): Observable<any> {
        // in a real world app, we may send the server to some remote logging infrastructure
        // instead of just logging it to the console
        return Observable.throw(error.json ? error.json() : 'Server error');
    }

    protected extractPayload(response) {
        return response.data || {}
    }

    get randInt() {
        return Math.floor(Math.random() * Math.floor(1000));
    }

    protected getList(page, url): Observable<any> {
        const current_page = page.pageNumber + 1;
        let params = new HttpParams();
        params = params.append('perPage', page.size);
        params = params.append('page', current_page);
        if (!_.isUndefined(page.sortParams)) {
            for (const key of Object.keys(page.sortParams)) {
                const value = page.sortParams[key];
                params = params.append('sort[]', key + ',' + value);
            }
        }
        if (!_.isUndefined(page.filterParams)) {
            for (const key of Object.keys(page.filterParams)) {
                const value = page.filterParams[key];
                if (value && value !== '') {
                    params = params.append(key, value);
                }
            }
        }
        return this.http.get(this.apiUrl + url, {params: params});
    }

    protected getRestList(page: Page, url): Observable<any> {
        let params = new HttpParams();
        params = params.append('perPage', page.size.toString());
        params = params.append('page', page.pageNumber.toString());
        params = params.append('timezoneOffset', (new Date()).getTimezoneOffset().toString()); // since backend uses UTC
        if (!_.isUndefined(page.sortParams)) {
            for (const key of Object.keys(page.sortParams)) {
                const value = page.sortParams[key];
                params = params.append('sort[]', key + ',' + value);
            }
        }
        return this.http.get(this.apiUrl + url, {params: this.applyFilters(page, params)});
    }

    protected applyFilters(page: Page, params: HttpParams) {
        if (!_.isUndefined(page.filterParams)) {
            for (const key of Object.keys(page.filterParams)) {
                const value = page.filterParams[key];
                if (value && value !== '' || value === 0) {
                    if (value instanceof Date) {
                        params = params.append(key, value.getFullYear() + '-' + (value.getMonth() + 1) + '-' + value.getDate());
                    } else {
                        params = params.append(key, value);
                    }
                }
            }
        }
        return params;
    }

    protected getXlsx(apiUrl: string, page: any, defaultFileName: string, subtype: string = '') {
        let params = new HttpParams();
        params = this.applyFilters(page, params);
        params = params.append('type', 'xls');
        params = params.append('subtype', subtype);

        return this.http.get(this.apiUrl + apiUrl, { params, responseType: 'blob', observe: 'response'} ).map(
            response => {
                const headers: HttpHeaders = response.headers;
                const disposition = headers.get('Content-Disposition');
                if (disposition) {
                    const match = disposition.match(/.*filename=\"?([^;\"]+)\"?.*/);
                    if (match[1]) {
                        defaultFileName = match[1];
                    }
                }
                defaultFileName = defaultFileName.replace(/[<>:"\/\\|?*]+/g, '_');
                const blob = new Blob([response.body], {type: 'application/vnd.ms-excel'});
                saveAs(blob, defaultFileName);
            })
            .catch(this.handleError);
    }

    protected getExportXlsData(url, preferences, page = null): Observable<any> {
        let params = new HttpParams();
        if (page) {
            params = this.applyFilters(page, params);
        }
        params = params.append('type', 'xls');
        params = params.append('subtype', '');
        params = params.append('exportMode', preferences.exportMode);
        params = params.append('col', preferences.columns);

        return this.http.get(this.apiUrl + url, {params, responseType: 'blob', observe: 'response'});
    }

    protected generateXls(xlsData: HttpResponse<Blob>, fileName: string) {
        const headers = xlsData.headers;
        const disposition = headers.get('Content-Disposition');
        if (disposition) {
            const match = disposition.match(/.*filename=\"?([^;\"]+)\"?.*/);
            if (match[1]) {
                fileName = match[1];
            }
        }
        fileName = fileName.replace(/[<>:"\/\\|?*]+/g, '_');
        const blob = new Blob([xlsData.body], {type: 'application/vns.ms-excel'});
        saveAs(blob, fileName);
    }

    protected getPdf(apiUrl: string, defaultFileName: string, page: any = null, autoPrint = false) {
        let params = new HttpParams();
        if (page) {
            params = this.applyFilters(page, params);
            params = params.append('perPage', page.size);
            params = params.append('page', page.pageNumber);
        }
        params = params.append('fileName', defaultFileName);

        return this.http.get(apiUrl, { params, responseType: 'blob', observe: 'response'} ).map(
            response => {
                const headers: HttpHeaders = response.headers;
                const disposition = headers.get('Content-Disposition');
                if (disposition) {
                    const match = disposition.match(/.*filename=\"?([^;\"]+)\"?.*/);
                    if (match[1]) {
                        defaultFileName = match[1];
                    }
                }
                defaultFileName = defaultFileName.replace(/[<>:"\/\\|?*]+/g, '_');
                const blob = new Blob([response.body], {type: 'application/pdf'});

                // IE doesn't allow using a blob object directly as link href
                // instead it is necessary to use msSaveOrOpenBlob
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob);
                    return;
                }

                // For other browsers:
                // Create a link pointing to the ObjectURL containing the blob.
                const data = window.URL.createObjectURL(blob);
                const w = window.open(data);
                if (autoPrint) {
                    w.print();
                }
            })
            .catch(this.handleError);
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/services/general.service.ts